import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check, ColorLens, Download, ImageOutlined } from '@mui/icons-material';
import {
  AppliedStorefrontPreset,
  integrationApiService,
  StorefrontPresetSummary,
} from '../../services/integrationApi';
import { ContrastReport, deriveColorSchemes, ShopifyColorSchemeSet } from '../../utils/brandColorSchemes';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../../utils/integrationSettingsCopy';
import { useToast } from '../../contexts/ToastContext';
import {
  CardGridSkeleton,
  EmptyState,
  InlineMessage,
  SectionCard,
  StatusChip,
} from '../ui';
import { tokens } from '../../theme/tokens';
import StorefrontMockPreview from './StorefrontMockPreview';

const GALLERY = COPY.presetGallery;

type WizardStep = 'gallery' | 'brand' | 'delivery';

export interface StorefrontPresetGalleryProps {
  integrationId: string;
  /**
   * The merchant's own store name, shown as the wordmark in the preview.
   * Falls back to the preset name so the mock never renders an empty header.
   */
  storeName?: string;
  appliedPreset?: AppliedStorefrontPreset | null;
  onApplied?: (applied: AppliedStorefrontPreset) => void;
}

function latestVersion(preset: StorefrontPresetSummary): { version: string; dawnVersion: string } {
  return preset.versions[0] || { version: '1.0.0', dawnVersion: 'unknown' };
}

function summaryFromApplied(applied: AppliedStorefrontPreset): StorefrontPresetSummary {
  return {
    id: applied.presetId,
    name: applied.presetName,
    description: '',
    screenshots: [],
    versions: [{ version: applied.version, dawnVersion: applied.dawnVersion }],
  };
}

function resolveDownloadVersion(
  preset: StorefrontPresetSummary,
  applied: AppliedStorefrontPreset | null,
  pinAppliedVersion: boolean,
): { version: string; dawnVersion: string } {
  if (pinAppliedVersion && applied?.presetId === preset.id) {
    const match = preset.versions.find((item) => item.version === applied.version);
    return match || { version: applied.version, dawnVersion: applied.dawnVersion };
  }
  return latestVersion(preset);
}

function toColorInputValue(hex: string, fallback: string): string {
  const cleaned = hex.trim().replace(/^#/, '');
  if (cleaned.length === 3 && /^[0-9a-fA-F]{3}$/.test(cleaned)) {
    return `#${cleaned.split('').map((char) => char + char).join('')}`;
  }
  if (cleaned.length === 6 && /^[0-9a-fA-F]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }
  return fallback;
}

const PresetMotif: React.FC<{ presetId: string }> = ({ presetId }) => {
  const dense = presetId === 'pro-shop' || presetId === 'mono';
  return (
    <Box
      sx={{
        height: 132,
        bgcolor: 'grey.50',
        p: dense ? 1 : 1.5,
        display: 'grid',
        gridTemplateColumns: dense ? 'repeat(3, 1fr)' : '1fr',
        gridTemplateRows: dense ? '1fr 1fr' : '1.4fr 1fr',
        gap: dense ? 0.75 : 1,
        mb: 1,
      }}
    >
      <Box sx={{ bgcolor: 'primary.main', gridColumn: dense ? '1 / -1' : undefined, opacity: 0.85 }} />
      <Box sx={{ bgcolor: 'grey.200' }} />
      {!dense && <Box sx={{ bgcolor: 'grey.300' }} />}
      {dense && (
        <>
          <Box sx={{ bgcolor: 'grey.200' }} />
          <Box sx={{ bgcolor: 'grey.300' }} />
          <Box sx={{ bgcolor: 'grey.200' }} />
        </>
      )}
    </Box>
  );
};

const ColorField: React.FC<{
  label: string;
  value: string;
  fallback: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
}> = ({ label, value, fallback, onChange, placeholder, onClear }) => {
  const swatch = value.trim() ? toColorInputValue(value, fallback) : fallback;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="input"
        type="color"
        aria-label={label}
        value={swatch}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          p: 0,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: 'pointer',
        }}
      />
      <TextField
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        size="small"
        fullWidth
        placeholder={placeholder}
        InputProps={
          onClear && value.trim()
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={onClear} sx={{ minWidth: 0 }}>
                      {GALLERY.clearAccentLabel}
                    </Button>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    </Stack>
  );
};

type StepState = 'done' | 'active' | 'upcoming';

/**
 * Three-step header shown at every stage, including the gallery, so the colour
 * step is visible before it is reachable — the gallery used to give no signal
 * that colours came next.
 */
const StepIndicator: React.FC<{
  step: WizardStep;
  presetName?: string;
  onBackToGallery: () => void;
}> = ({ step, presetName, onBackToGallery }) => {
  const stateFor = (target: WizardStep): StepState => {
    const order: WizardStep[] = ['gallery', 'brand', 'delivery'];
    const current = order.indexOf(step);
    const index = order.indexOf(target);
    if (index < current) return 'done';
    return index === current ? 'active' : 'upcoming';
  };

  const cells: Array<{
    key: WizardStep;
    number: string;
    label: string;
    value: React.ReactNode;
  }> = [
    {
      key: 'gallery',
      number: '1',
      label: GALLERY.steps.look.label,
      value:
        stateFor('gallery') === 'done' && presetName ? (
          <>
            {presetName}
            {' · '}
            <Link component="button" type="button" variant="caption" onClick={onBackToGallery}>
              {GALLERY.steps.changeLink}
            </Link>
          </>
        ) : (
          GALLERY.steps.look.value
        ),
    },
    {
      key: 'brand',
      number: '2',
      label: GALLERY.steps.brand.label,
      value: stateFor('brand') === 'active' ? GALLERY.steps.inProgress : GALLERY.steps.brand.value,
    },
    {
      key: 'delivery',
      number: '3',
      label: GALLERY.steps.download.label,
      value:
        stateFor('delivery') === 'active' ? GALLERY.steps.inProgress : GALLERY.steps.download.value,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {cells.map((cell, index) => {
        const state = stateFor(cell.key);
        return (
          <Stack
            key={cell.key}
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
              px: 2.75,
              py: 1.75,
              position: 'relative',
              borderLeft: index === 0 ? undefined : { sm: '1px solid' },
              borderTop: index === 0 ? undefined : { xs: '1px solid', sm: 'none' },
              borderColor: 'divider',
              ...(state === 'active'
                ? {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 2,
                      bgcolor: 'primary.main',
                    },
                  }
                : {}),
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
                ...(state === 'done'
                  ? { bgcolor: 'success.light', color: 'success.dark' }
                  : state === 'active'
                    ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
                    : { border: '1px solid', borderColor: 'divider', color: 'text.disabled' }),
              }}
            >
              {state === 'done' ? <Check sx={{ fontSize: 14 }} /> : cell.number}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: 12,
                  color: state === 'active' ? 'primary.main' : 'text.secondary',
                  fontWeight: state === 'upcoming' ? 400 : 600,
                }}
              >
                {cell.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: 13, color: 'text.primary' }}
              >
                {cell.value}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Box>
  );
};

/**
 * "{n} colour pairs checked · {k} adjusted for readability" — the engine walks
 * a seed until it clears AA, so this says how much of the merchant's palette
 * survived as picked.
 */
function contrastNote(contrast: ContrastReport[]): string {
  const adjusted = contrast.filter((item) => !item.passedUnadjusted).length;
  const checked = GALLERY.contrastChecked.replace('{n}', String(contrast.length));
  const tail =
    adjusted === 0
      ? GALLERY.contrastAllPass
      : GALLERY.contrastAdjusted.replace('{k}', String(adjusted));
  return `${checked} · ${tail}`;
}

const StorefrontPresetGallery: React.FC<StorefrontPresetGalleryProps> = ({
  integrationId,
  storeName,
  appliedPreset,
  onApplied,
}) => {
  const { showToast } = useToast();
  const defaultPrimary = tokens.accent.main;
  const [presets, setPresets] = useState<StorefrontPresetSummary[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [step, setStep] = useState<WizardStep>('gallery');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pinAppliedVersion, setPinAppliedVersion] = useState(false);
  const [primary, setPrimary] = useState<string>(defaultPrimary);
  const [accent, setAccent] = useState('');
  const [logoSrc, setLogoSrc] = useState<string | undefined>();
  const [downloading, setDownloading] = useState(false);
  const [localApplied, setLocalApplied] = useState<AppliedStorefrontPreset | null>(appliedPreset ?? null);

  useEffect(() => {
    setLocalApplied(appliedPreset ?? null);
  }, [appliedPreset]);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    integrationApiService
      .getStorefrontPresets()
      .then((data) => {
        if (cancelled) return;
        setPresets(data);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applied = localApplied;
  const selectedFromGallery = presets.find((preset) => preset.id === selectedId) || null;
  const selected =
    selectedFromGallery
    || (applied && selectedId === applied.presetId ? summaryFromApplied(applied) : null);
  const derived = useMemo(() => {
    try {
      return deriveColorSchemes({
        primary,
        ...(accent.trim() ? { accent: accent.trim() } : {}),
      });
    } catch {
      return null;
    }
  }, [primary, accent]);

  // A half-typed hex is invalid for one keystroke. Blanking the preview mid-type
  // reads as a crash, so hold the last valid schemes until the input parses again.
  const lastValidSchemes = useRef<ShopifyColorSchemeSet | null>(null);
  if (derived) lastValidSchemes.current = derived.schemes;
  const previewSchemes = derived?.schemes ?? lastValidSchemes.current;
  // The accent is derived from the primary hue when the merchant leaves it blank;
  // scheme-5's surface IS that derived accent, so the field can show what it got.
  const derivedAccent = derived?.schemes['scheme-5'].background;
  const previewStoreName = storeName?.trim() || selected?.name || '';

  const openBrand = (
    preset: StorefrontPresetSummary,
    colors?: AppliedStorefrontPreset['colors'],
    options?: { pinAppliedVersion?: boolean },
  ) => {
    setSelectedId(preset.id);
    setPrimary(colors?.primary || applied?.colors.primary || defaultPrimary);
    setAccent(colors?.accent || '');
    setPinAppliedVersion(options?.pinAppliedVersion === true);
    setStep('brand');
  };

  const handleLogo = (file: File | null) => {
    if (!file) {
      setLogoSrc(undefined);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setLogoSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async () => {
    if (!selected || downloading) return;
    setDownloading(true);
    const versionInfo = resolveDownloadVersion(selected, applied, pinAppliedVersion);
    const colors = {
      primary,
      ...(accent.trim() ? { accent: accent.trim() } : {}),
    };
    try {
      await integrationApiService.downloadThemeZip(integrationId, {
        presetId: selected.id,
        version: versionInfo.version,
        ...colors,
      });
      const nextApplied: AppliedStorefrontPreset = {
        presetId: selected.id,
        presetName: selected.name,
        version: versionInfo.version,
        dawnVersion: versionInfo.dawnVersion,
        colors,
        appliedAt: new Date().toISOString(),
      };
      setLocalApplied(nextApplied);
      onApplied?.(nextApplied);
      setPinAppliedVersion(false);
      showToast(`Downloaded ${selected.name}. Upload the zip in Shopify to publish.`, 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to build theme zip';
      showToast(message.includes('could not be saved') ? GALLERY.persistFailed : message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleReapply = async () => {
    if (!applied || downloading) return;
    const preset = presets.find((item) => item.id === applied.presetId);
    if (preset) setSelectedId(preset.id);
    setPrimary(applied.colors.primary);
    setAccent(applied.colors.accent || '');
    setDownloading(true);
    try {
      await integrationApiService.downloadThemeZip(integrationId, {
        presetId: applied.presetId,
        version: applied.version,
        ...applied.colors,
      });
      const nextApplied: AppliedStorefrontPreset = {
        ...applied,
        appliedAt: new Date().toISOString(),
      };
      setLocalApplied(nextApplied);
      onApplied?.(nextApplied);
      showToast(`Downloaded ${applied.presetName}. Upload the zip in Shopify to publish.`, 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to build theme zip';
      showToast(message.includes('could not be saved') ? GALLERY.persistFailed : message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleChangeColors = () => {
    if (!applied) return;
    const preset = presets.find((item) => item.id === applied.presetId) || summaryFromApplied(applied);
    openBrand(preset, applied.colors, { pinAppliedVersion: true });
  };

  return (
    <SectionCard
      dense
      padded
      leading={<ColorLens fontSize="small" color="action" />}
      title={GALLERY.title}
      subtitle={GALLERY.subtitle}
    >
      <Stack spacing={2.5}>
        <StepIndicator
          step={step}
          presetName={selected?.name}
          onBackToGallery={() => setStep('gallery')}
        />

        {applied ? (
          <SectionCard
            dense
            padded
            title={GALLERY.appliedTitle}
            subtitle={GALLERY.appliedMeta
              .replace('{version}', applied.version)
              .replace('{dawn}', applied.dawnVersion)}
            leading={<StatusChip label={applied.presetName} tone="info" />}
            actions={
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={handleReapply}>
                  {GALLERY.reapplyLabel}
                </Button>
                <Button size="small" variant="text" onClick={handleChangeColors}>
                  {GALLERY.changeColorsLabel}
                </Button>
              </Stack>
            }
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
              <StatusChip
                tone="neutral"
                variant="outlined"
                label={`${GALLERY.appliedPrimary} ${toColorInputValue(applied.colors.primary, defaultPrimary)}`}
              />
              {applied.colors.accent && (
                <StatusChip
                  tone="neutral"
                  variant="outlined"
                  label={`${GALLERY.appliedAccent} ${toColorInputValue(applied.colors.accent, defaultPrimary)}`}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {GALLERY.appliedNote}
            </Typography>
          </SectionCard>
        ) : (
          <EmptyState size="compact" title={GALLERY.appliedEmpty} />
        )}

        {loadState === 'loading' && <CardGridSkeleton count={4} minWidth={200} lines={2} />}
        {loadState === 'error' && (
          <InlineMessage severity="error">{GALLERY.galleryLoadError}</InlineMessage>
        )}
        {loadState === 'ready' && presets.length === 0 && (
          <EmptyState size="compact" title={GALLERY.galleryEmpty} />
        )}

        {loadState === 'ready' && step === 'gallery' && presets.length > 0 && (
          <Box>
            {presets.every((preset) => preset.screenshots.length === 0) && (
              <EmptyState
                size="compact"
                icon={<ImageOutlined />}
                title={GALLERY.screenshotPending}
                sx={{ py: 1, mb: 1 }}
              />
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
              }}
            >
            {presets.map((preset) => {
              const versionInfo = latestVersion(preset);
              const isCurrent = applied?.presetId === preset.id;
              return (
                <SectionCard
                  key={preset.id}
                  dense
                  padded
                  title={preset.name}
                  subtitle={preset.description}
                  leading={isCurrent ? <StatusChip label="Applied" tone="positive" /> : undefined}
                  actions={
                    <Button size="small" variant="outlined" onClick={() => openBrand(preset)}>
                      {GALLERY.chooseLook}
                    </Button>
                  }
                  sx={isCurrent ? { outline: '1px solid', outlineColor: 'primary.main' } : undefined}
                >
                  {preset.screenshots[0] ? (
                    <>
                      <Box
                        component="img"
                        src={preset.screenshots[0]}
                        alt=""
                        sx={{ width: '100%', height: 132, objectFit: 'cover', display: 'block' }}
                      />
                      {/* Colours are derived per merchant, so the shot is illustrative.
                          Saying so here also signals that a colour step follows. */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, mb: 1 }}
                      >
                        {GALLERY.exampleColoursCaption}
                      </Typography>
                    </>
                  ) : (
                    <PresetMotif presetId={preset.id} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {versionInfo.version} · Dawn {versionInfo.dawnVersion}
                  </Typography>
                </SectionCard>
              );
            })}
            </Box>
          </Box>
        )}

        {step !== 'gallery' && selected && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">{selected.name}</Typography>

            {step === 'brand' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {GALLERY.brandIntro}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'repeat(auto-fit, minmax(260px, 1fr))',
                    },
                    gap: 1.5,
                  }}
                >
                  <ColorField
                    label={GALLERY.primaryLabel}
                    value={primary}
                    fallback={defaultPrimary}
                    onChange={setPrimary}
                  />
                  <ColorField
                    label={GALLERY.accentLabel}
                    value={accent}
                    fallback={derivedAccent || defaultPrimary}
                    onChange={setAccent}
                    onClear={() => setAccent('')}
                    placeholder={
                      derivedAccent
                        ? GALLERY.accentDerivedPlaceholder.replace('{hex}', derivedAccent)
                        : undefined
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>
                    {GALLERY.logoLabel}
                  </Typography>
                  <Button component="label" size="small" variant="outlined">
                    Upload logo
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => handleLogo(event.target.files?.[0] || null)}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {GALLERY.logoHint}
                  </Typography>
                </Box>
                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                    spacing={0.5}
                    sx={{ mb: 0.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {GALLERY.previewTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {GALLERY.previewInYourColours.replace('{name}', selected.name)}
                      </Typography>
                    </Stack>
                    {derived && (
                      <Typography variant="caption" color="text.secondary">
                        {contrastNote(derived.contrast)}
                      </Typography>
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: 12, mb: 1 }}
                  >
                    {GALLERY.previewCaption}
                  </Typography>
                  {previewSchemes ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <StorefrontMockPreview
                        presetId={selected.id}
                        storeName={previewStoreName}
                        logoSrc={logoSrc}
                        schemes={previewSchemes}
                      />
                    </Box>
                  ) : (
                    <EmptyState size="compact" title="Enter a valid colour to preview this look." />
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button onClick={() => setStep('gallery')}>{GALLERY.backToGallery}</Button>
                  <Button variant="contained" onClick={() => setStep('delivery')} disabled={!derived}>
                    {GALLERY.continueToDownload}
                  </Button>
                </Stack>
              </>
            )}

            {step === 'delivery' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {GALLERY.deliveryIntro}
                </Typography>
                {previewSchemes && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <StorefrontMockPreview
                      presetId={selected.id}
                      storeName={previewStoreName}
                      logoSrc={logoSrc}
                      schemes={previewSchemes}
                    />
                  </Box>
                )}
                <Button
                  variant="contained"
                  startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                  onClick={handleDownload}
                  disabled={downloading || !derived}
                >
                  {downloading ? GALLERY.downloadingLabel : GALLERY.downloadLabel}
                </Button>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    {GALLERY.uploadTitle}
                  </Typography>
                  <Box component="ol" sx={{ m: 0, pl: 2.25 }}>
                    {GALLERY.uploadSteps.map((item) => (
                      <Typography key={item} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Button onClick={() => setStep('brand')}>Back to colours</Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default StorefrontPresetGallery;
